import {
    ApolloCache,
    ApolloClient,
    ApolloClientOptions,
    ApolloLink,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject,
    from,
} from '@apollo/client';
import { IncomingMessage } from 'http';
import { getCookieByName, isSsr } from '@99designs/browser';
import { default as introspectionResult } from './generated/introspectionResult.generated';
import { typePolicies } from './generated/typePolicies.generated';

export const DEFAULT_GRAPH_ENDPOINT = '/app/graphql';

const hasScheme = /^https?:\/\//;

const getCsrfToken = (cookie: string) => getCookieByName('_99csrf', cookie);

/**
 * Create Apollo client for SSR
 *
 * @param {string} host URI (e.g. 'https://example.com')
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createFragmentSSRApolloClient(
    host: string,
    options?: Partial<ApolloClientOptions<NormalizedCacheObject>>
): ApolloClient<NormalizedCacheObject> {
    if (host.length <= 0) {
        throw new Error('host is required but not provided.');
    }

    if (!hasScheme.test(host)) {
        host = `https://${host}`;
    }

    const headers: Record<string, string> = {
        cookie: '',
        experiments: '',
        '99-request-summary': '',
        '99-request-summary-sig': '',
        'x-csrf-token': '',
    };

    return createApolloClient(host, headers, options);
}

/**
 * Create Apollo client for SSR
 *
 * @param {IncomingMessage} req Request
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createSSRApolloClient(
    req: IncomingMessage,
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    options?: Partial<ApolloClientOptions<NormalizedCacheObject>>
): ApolloClient<NormalizedCacheObject> {
    const getHeader = (name: string): string => (req.headers[name] || '') as string;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let host = process.env.GRAPHQL_HOST
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          process.env.GRAPHQL_HOST
        : getHeader('x-forwarded-host');

    if (host.length <= 0) {
        throw new Error(
            'X-Forwarded-Host header is required but not present in request. This normally means the request has not been proxied through bastion'
        );
    }

    if (!hasScheme.test(host)) {
        host = `https://${host}`;
    }

    const headers: Record<string, string> = {
        cookie: getHeader('cookie'),
        experiments: getHeader('experiments'),
        '99-request-summary': getHeader('99-request-summary'),
        '99-request-summary-sig': getHeader('99-request-summary-sig'),
        'x-csrf-token': getCsrfToken(getHeader('cookie')),
    };

    return createApolloClient(`${host}${endpoint}`, headers, options);
}

// Filters out individual cookies containing non-ISO-8859-1 characters (code point > 0xFF)
// to prevent fetch from throwing "String contains non ISO-8859-1 code point" when the
// cookie string is passed as a request header value.
export const sanitiseCookieHeader = (cookieString: string): string =>
    cookieString
        .split('; ')
        .filter((c) => !Array.from(c).some((ch) => ch.charCodeAt(0) > 0xff))
        .join('; ');

/**
 * Create Apollo client for browser
 *
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createBrowserApolloClient(
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    options?: Partial<ApolloClientOptions<NormalizedCacheObject>>,
    errorLink?: ApolloLink,
    cache?: ApolloCache<NormalizedCacheObject>
): ApolloClient<NormalizedCacheObject> {
    const rawCookie = typeof document !== 'undefined' ? document.cookie : '';
    const headers = {
        cookie: sanitiseCookieHeader(rawCookie),
        'x-csrf-token': getCsrfToken(rawCookie),
    };

    return createApolloClient(endpoint, headers, options, errorLink, cache);
}

/**
 * Create custom Apollo client
 *
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Record<string, string>} headers Optional list of headers (default: {})
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client (default: {})
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createApolloClient(
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    headers: Record<string, string> = {},
    options: Partial<ApolloClientOptions<NormalizedCacheObject>> = {},
    errorLink?: ApolloLink,
    cache?: ApolloCache<NormalizedCacheObject>
): ApolloClient<NormalizedCacheObject> {
    const browserCookie = typeof document !== 'undefined' ? document.cookie : '';

    const link = new HttpLink({
        uri: endpoint,
        credentials: 'same-origin',
        headers: {
            ...(options?.headers || {}),
            '99-request-summary': headers?.['99-request-summary'],
            '99-request-summary-sig': headers?.['99-request-summary-sig'],
            'x-csrf-token':
                headers?.['x-csrf-token'] ||
                getCsrfToken(headers?.['cookie'] || '') ||
                getCsrfToken(browserCookie),
            cookie: headers?.['cookie'] || browserCookie,
            experiments: headers?.['experiments'],
        },
    });

    return new ApolloClient({
        link: errorLink ? from([errorLink, link]) : link,
        cache: cache ?? createInMemoryCache(),
        defaultOptions: {
            query: {
                fetchPolicy: 'cache-first',
                errorPolicy: 'all',
            },
        },
        ssrMode: isSsr(),
        ...options,
    });
}

export function createInMemoryCache(): ApolloCache<NormalizedCacheObject> {
    return new InMemoryCache({
        addTypename: true,
        typePolicies: typePolicies,
        possibleTypes: introspectionResult.possibleTypes,
    });
}
